import axios from "axios";
import { getApiUrl } from "../utils/envConfig";
import {
  validateSecurityHeaders,
  getApiSecurityHeaders,
} from "../config/securityHeaders";
import { getAuthToken, refreshAuthToken, waitForUser } from "../utils/authUtils";
import { isUnrecoverableAuthFailure } from "../utils/authFailureCodes";
import type { components, operations } from "./generated/schema";

// Cached store reference for impersonation header injection (avoids dynamic import per request)
let _storeRef: any = null;

// Enhanced axios utility with token management and retry logic. Kept local to
// authApi (not the shared helper) because this path must inject the
// X-Impersonation-Session header so the audit middleware can track impersonated
// requests — behavior the other api modules' helpers don't share.
export const makeAuthenticatedRequest = async (
  url: string,
  options: any = {},
  retries = 3
) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    // Inject impersonation session header if active.
    // This lets the audit middleware track every request during impersonation.
    const impersonationHeaders: any = {};
    try {
      if (!_storeRef) {
        const mod: any = await import("../store/store");
        // Named export — `store/store.js` has no default. Reading `.default`
        // here silently dropped the impersonation header below, because the
        // surrounding catch treats an unavailable store as "not impersonating".
        _storeRef = mod.store;
      }
      const authState = _storeRef.getState()?.auth;
      if (authState?.isImpersonating && authState?.impersonationSessionId) {
        impersonationHeaders["X-Impersonation-Session"] = authState.impersonationSessionId;
      }
    } catch {
      // Store not available yet (during init) — skip
    }

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...impersonationHeaders,
      ...options.headers,
    };

    const requestConfig: any = {
      url,
      headers,
      method: options.method || "GET",
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development
    if (import.meta.env.VITE_ENVIRONMENT === "development") {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error: any) {
    // Ahead of the 4xx branch below, which returns on any 401 and would leave
    // the dead session in place. A revoked or idle-timed-out session cannot be
    // refreshed into a working one, so sign out: that clears the persisted
    // record, which is what lets the next sign-in mint a usable token.
    if (
      isUnrecoverableAuthFailure(error.response?.status, error.response?.data)
    ) {
      const { handleSessionRevoked } = await import("../utils/sessionRevoked");
      await handleSessionRevoked();
      const sessionError: any = new Error("Session ended");
      sessionError.status = error.response?.status;
      sessionError.code = error.response?.data?.code;
      throw sessionError;
    }

    // For client errors (4xx), don't retry — surface the backend's detail
    // message AND its structured fields so callers can branch on a machine
    // code (e.g. "invite_domain_not_allowed") instead of matching prose.
    if (error.response && error.response.status >= 400 && error.response.status < 500) {
      const data = error.response.data;
      const detail = data?.detail || error.message;
      const clientError: any = new Error(
        typeof detail === "string" ? detail : "Request failed",
      );
      clientError.status = error.response.status;
      clientError.code = data?.code;
      throw clientError;
    }

    if (retries <= 0) {
      throw new Error("Max retries reached. Could not complete the request.");
    }

    // Try to refresh token and retry
    try {
      await refreshAuthToken();
      // Retry the request with the new token
      return await makeAuthenticatedRequest(url, options, retries - 1);
    } catch (tokenError: any) {
      throw new Error("Failed to refresh token: " + tokenError.message);
    }
  }
};

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types).
type CompaniesResponse =
  operations["get_unique_companies_route_client__id_client__country__country__brands_companies_get"]["responses"][200]["content"]["application/json"];
type BrandsResponse =
  operations["get_brands_by_company_route_client__id_client__country__country__brands_get"]["responses"][200]["content"]["application/json"];
type SubbrandsResponse =
  operations["get_subbrands_by_brand_route_client__id_client__country__country__subbrands__brand__get"]["responses"][200]["content"]["application/json"];
type SubbrandDetailsResponse = components["schemas"]["SubbrandDetailsResponse"];
type VolumesResponse =
  operations["get_volumes_by_subbrand_route_client__id_client__country__country__subbrands__id_subbrand__volumes_get"]["responses"][200]["content"]["application/json"];
type PackagingTypesResponse =
  operations["get_packaging_type_by_subbrand_route_client__id_client__country__country__subbrands__id_subbrand__packaging_types_get"]["responses"][200]["content"]["application/json"];
type PackagingSizesResponse =
  operations["get_packaging_size_by_subbrand_route_client__id_client__country__country__subbrands__id_subbrand__packaging_sizes_get"]["responses"][200]["content"]["application/json"];

// Authentication utilities are now imported from ../utils/authUtils

// Mock drinks data
const mockDrinks = {
  coca_cola_regular: {
    availability: 60,
    bundles: 50,
    content: 70,
    name: "Coca-Cola Regular 330ml Can",
    total_dsf_count: 20997,
  },
};

// Builds the drink tab list, with the PET_Performance tab always first
const transformToDrinkData = (data) => {
  const drinks = [
    {
      drink: {
        id: "PET_Performance",
        name: "ONE Performance",
      },
    },
  ];

  const skuIds = Object.keys(data);

  skuIds.forEach((skuId) => {
    const drink = {
      drink: {
        ...data[skuId],
        id: skuId,
      },
    };

    drinks.push(drink);
  });

  return drinks;
};

// waitForUser and getAuthToken are now imported from ../utils/authUtils

export const fetchOutletsData = async ({
  page,
  page_size,
  filterOptions,
  sorterOptions,
  searchTerm,
  country,
  id_client,
  company,
  sku_parameters,
}): Promise<any> => {
  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    const url = `${getApiUrl()}/metrics/agg_outlet_skus?id_client=${id_client}&country=${country}&page=${page}&limit=${page_size}&company=${company}${searchParam}`;

    const bodyPayload = {
      ...filterOptions,
      ...sorterOptions,
    };

    if (sku_parameters) {
      bodyPayload.sku_parameters = {
        company: sku_parameters.company ?? null,
        brand: sku_parameters.brand ?? null,
        subbrand: sku_parameters.subbrand ?? null,
        packaging_type: sku_parameters.packaging_type ?? null,
        packaging_size: sku_parameters.packaging_size ?? null,
        volume: sku_parameters.volume ?? null,
      };
    }

    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(bodyPayload),
    });

    return data;
  } catch (error) {
    console.error(error);

    throw new Error("Failing fetching outlet data: " + error.message);
  }
};

// Serves the mocked drinks above; there is no backend endpoint behind this
export const fetchDrinksData = async (): Promise<any> => {
  try {
    const data = await new Promise((resolve) => {
      setTimeout(() => {
        resolve(mockDrinks);
      }, 1000);
    });
    return transformToDrinkData(data);
  } catch {
    throw new Error("Failing fetching drink data");
  }
};

export const fetchFilterDimensionsData = async ({
  id_client,
  country,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/filter-dimensions?id_client=${id_client}&country=${country}`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failing fetching filter dimensions: " + error.message);
  }
};

export const fetchFilterDimensionsByCountry = async ({
  countries,
  id_client,
}): Promise<any> => {
  try {
    const searchParams = new URLSearchParams();
    if (Array.isArray(countries)) {
      countries.forEach((country) => searchParams.append("country", country));
    } else {
      searchParams.append("country", countries);
    }

    // Add id_client parameter
    searchParams.append("id_client", id_client);

    const url = `${getApiUrl()}/filter-dimensions?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error(
      "Failing fetching filter dimensions by countries: " + error.message
    );
  }
};

// Fetch user data and extract company (id_client) value
export const fetchUserData = async (withScope = true): Promise<any> => {
  try {
    // Wait for Firebase to restore auth session (important on page refresh!)
    const currentUser = await waitForUser();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const userId = currentUser.uid;
    const url = `${getApiUrl()}/users/${userId}?with_scope=${withScope}`;

    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failed to fetch user data: " + error.message);
  }
};

export const createSavedList = async ({
  id_client,
  user_id,
  name,
  id_outlet_info,
  applied_filters,
  selected_skus,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${user_id}/saved_lists`;

    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify({
        name: name,
        id_outlet_info,
        applied_filters: { ...applied_filters },
        selected_skus,
      }),
    });

    return data;
  } catch (error) {
    throw new Error("Failing create new list: " + error.message);
  }
};

export const GetAllSavedLists = async ({
  id_client,
  user_id,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${user_id}/saved_lists`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failing get all lists: " + error.message);
  }
};

export const GetListTableData = async ({
  id_list,
  country,
  id_client,
  user_id,
}): Promise<any> => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "latest",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    throw new Error("Failing get list table data: " + error.message);
  }
};

export const GetListRefreshData = async ({
  id_list,
  country,
  id_client,
  user_id,
}): Promise<any> => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "history",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    throw new Error("Failing get list refresh data: " + error.message);
  }
};

export const GetListSummaryData = async ({
  id_list,
  country,
  id_client,
  user_id,
}): Promise<any> => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "detail_summary",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    throw new Error("Failing get list summary data: " + error.message);
  }
};

export const DeleteList = async ({
  id_client,
  id_user,
  id_list,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${id_user}/saved_lists/${id_list}`;
    const data = await makeAuthenticatedRequest(url, { method: "DELETE" });
    return data;
  } catch (error) {
    throw new Error("Failing deleting list: " + error.message);
  }
};

export const CreateGoogleUserDoc = async ({
  id_client,
  email,
  id_user,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/users`;

    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify({
        auth_method: "google",
        company: id_client,
        email: email,
        id_user: id_user,
        id_client: {
          ko_global: {
            role: "super_admin",
          },
        },
      }),
    });

    return data;
  } catch (error) {
    throw new Error("Failing creating google user doc: " + error.message);
  }
};

export const fetchSigleOutletsData = async ({
  id_outlet,
  country,
  id_client,
}): Promise<any> => {
  try {
    const storeAvailabilityUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/store_availability?country=${country}`;
    const skuListUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/sku_list?country=${country}`;
    const skuInfoUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/info?country=${country}`;
    const storeAvailabilityData = await makeAuthenticatedRequest(
      storeAvailabilityUrl
    );
    const skuListData = await makeAuthenticatedRequest(skuListUrl);
    const skuInfoData = await makeAuthenticatedRequest(skuInfoUrl);

    return {
      store_availbility: storeAvailabilityData,
      sku_list: skuListData,
      sku_info: skuInfoData,
    };
  } catch (error) {
    throw new Error("Failing fetching single outlet data: " + error.message);
  }
};

export const fetchSKUCompanies = async ({
  id_client,
  country,
}): Promise<CompaniesResponse> => {
  try {
    const companiesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/brands/companies`;
    const companiesListData = await makeAuthenticatedRequest(companiesUrl);
    return companiesListData;
  } catch (error) {
    throw new Error("Failing fetching sku companies: " + error.message);
  }
};

export const fetchSKUBrands = async ({
  id_client,
  company,
  country,
}): Promise<BrandsResponse> => {
  try {
    // company is a query param, not a path segment: names like "Carlsberg A/S"
    // contain "/", which would split the path and truncate the value server-side.
    const params = new URLSearchParams({ company });
    const brandsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/brands?${params.toString()}`;
    const brandsListData = await makeAuthenticatedRequest(brandsUrl);
    return brandsListData;
  } catch (error) {
    throw new Error("Failing fetching sku brands: " + error.message);
  }
};

export const fetchSKUSubbrands = async ({
  id_client,
  id_brand,
  country,
}): Promise<SubbrandsResponse> => {
  try {
    const subbrandsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_brand}`;
    const subbrandsListData = await makeAuthenticatedRequest(subbrandsUrl);
    return subbrandsListData;
  } catch (error) {
    throw new Error("Failing fetching sku subbrands: " + error.message);
  }
};

export const fetchSKUDetails = async ({
  id_client,
  id_subbrand,
  country,
}): Promise<SubbrandDetailsResponse> => {
  try {
    const detailsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/details`;
    const detailsListData = await makeAuthenticatedRequest(detailsUrl);
    return detailsListData;
  } catch (error) {
    throw new Error("Failing fetching sku details: " + error.message);
  }
};

export const fetchSKUVolumes = async ({
  id_client,
  id_subbrand,
  country,
  packaging_type,
  packaging_size,
}): Promise<VolumesResponse> => {
  try {
    const params = new URLSearchParams();
    if (packaging_type) {
      params.append("packaging_type", packaging_type);
    }
    if (packaging_size) {
      params.append("packaging_size", packaging_size);
    }
    const queryString = params.toString();
    const volumesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/volumes${
      queryString ? `?${queryString}` : ""
    }`;
    const volumesListData = await makeAuthenticatedRequest(volumesUrl);
    return volumesListData;
  } catch (error) {
    throw new Error("Failing fetching sku volumes: " + error.message);
  }
};

export const fetchSKUPackagingTypes = async ({
  id_client,
  id_subbrand,
  country,
  volume,
  packaging_size,
}): Promise<PackagingTypesResponse> => {
  try {
    const params = new URLSearchParams();
    if (volume) {
      params.append("volume", volume);
    }
    if (packaging_size) {
      params.append("packaging_size", packaging_size);
    }
    const queryString = params.toString();
    const packagingTypesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/packaging-types${
      queryString ? `?${queryString}` : ""
    }`;
    const packagingTypesData = await makeAuthenticatedRequest(
      packagingTypesUrl
    );
    return packagingTypesData;
  } catch (error) {
    throw new Error("Failing fetching sku packaging types: " + error.message);
  }
};

export const fetchSKUPackagingSizes = async ({
  id_client,
  id_subbrand,
  country,
  volume,
  packaging_type,
}): Promise<PackagingSizesResponse> => {
  try {
    const params = new URLSearchParams();
    if (volume) {
      params.append("volume", volume);
    }
    if (packaging_type) {
      params.append("packaging_type", packaging_type);
    }
    const queryString = params.toString();
    const packagingSizesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/packaging-sizes${
      queryString ? `?${queryString}` : ""
    }`;
    const packagingSizesData = await makeAuthenticatedRequest(
      packagingSizesUrl
    );
    return packagingSizesData;
  } catch (error) {
    throw new Error("Failing fetching sku packaging sizes: " + error.message);
  }
};

